import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import type { Lang, ModeId } from '@polyglot/engine';
import { MODES, validateLanguageSelection } from '@polyglot/engine';
import { BackLink, Spinner } from '../components/ui';
import { startLocalSession } from '../game/localSession';
import { api, ApiError, ensureSession } from '../lib/api';
import { useSettings } from '../lib/settings';
import { ContractPreview, LanguagePicker } from './Setup';

type LiveMode = Exclude<ModeId, 'relax'>;
const FALLBACK_AFTER_MS = 20_000;

export function rememberInvite(matchId: string, url: string): void {
  try {
    sessionStorage.setItem(`pa.invite.${matchId}`, url);
  } catch {
    /* ignore */
  }
}

export function recallInvite(matchId: string): string | null {
  try {
    return sessionStorage.getItem(`pa.invite.${matchId}`);
  } catch {
    return null;
  }
}

export function LiveHub() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const settings = useSettings();
  const [mode, setMode] = useState<LiveMode>('classic');
  const def = MODES[mode];
  const [langs, setLangs] = useState<Lang[]>(settings.lastLanguages.slice(0, def.maxLanguages));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<string | null>(null);
  const [waitingSince, setWaitingSince] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [searching, setSearching] = useState<number | null>(null);
  const [expired, setExpired] = useState(false);
  const pollRef = useRef<number | null>(null);
  const ticketRef = useRef<string | null>(null);
  const selectionError = validateLanguageSelection(def, langs);

  // Leaving the page (navigation, tab closed) cancels the search: nobody is paired with a
  // player who is gone. The server also ignores tickets that stop polling (heartbeat).
  useEffect(() => {
    const leave = () => {
      const t = ticketRef.current;
      if (!t) return;
      ticketRef.current = null;
      void fetch(`/api/live/queue/${t}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('pagehide', leave);
    return () => {
      window.removeEventListener('pagehide', leave);
      if (pollRef.current) window.clearInterval(pollRef.current);
      leave();
    };
  }, []);

  const createRoom = async () => {
    setBusy(true);
    setError(null);
    try {
      await ensureSession(settings.uiLang);
      const r = await api<{ matchId: string; inviteUrl: string }>('POST', '/api/live/rooms', {
        mode,
        languages: def.languagePolicy === 'shared' ? langs : undefined,
      });
      rememberInvite(r.matchId, r.inviteUrl);
      navigate(`/match/${r.matchId}`);
    } catch {
      setError(t('errors.generic'));
      setBusy(false);
    }
  };

  const stopPolling = () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const quick = async () => {
    setBusy(true);
    setError(null);
    setExpired(false);
    setSearching(null);
    try {
      await ensureSession(settings.uiLang);
      const r = await api<{ ticket: string; matchId: string | null }>('POST', '/api/live/queue', {
        mode,
        languages: langs,
      });
      if (r.matchId) return navigate(`/match/${r.matchId}`);
      ticketRef.current = r.ticket;
      setTicket(r.ticket);
      setWaitingSince(Date.now());
      stopPolling();
      // Polling is also the heartbeat that keeps the ticket eligible on the server.
      pollRef.current = window.setInterval(async () => {
        setNow(Date.now());
        try {
          const s = await api<{ matchId: string | null; cancelled: boolean; searching: number }>(
            'GET',
            `/api/live/queue/${r.ticket}`,
          );
          if (s.matchId) {
            stopPolling();
            ticketRef.current = null;
            navigate(`/match/${s.matchId}`);
          } else if (s.cancelled) {
            // Maximum wait reached (or cancelled elsewhere): stop and offer the alternatives.
            stopPolling();
            ticketRef.current = null;
            setExpired(true);
          } else setSearching(s.searching);
        } catch {
          /* keep polling */
        }
      }, 1500);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 0 ? t('online.offlineNote') : t('errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    stopPolling();
    const current = ticketRef.current ?? ticket;
    ticketRef.current = null;
    if (current) await api('DELETE', `/api/live/queue/${current}`).catch(() => {});
    setTicket(null);
    setExpired(false);
  };

  if (ticket) {
    const waited = now - waitingSince;
    const fallback = (
      <section className="card fallback">
        <p>{expired ? t('live.noOpponentFound') : t('live.fallbackTitle')}</p>
        <div className="results-actions">
          <button
            className="btn primary"
            onClick={async () => {
              await cancel();
              await startLocalSession({
                mode,
                languages: langs,
                bot: { level: 'casual', languages: langs },
              });
              navigate('/play');
            }}
          >
            ⚙ {t('live.fallbackRobot')}
          </button>
          <button
            className="btn ghost"
            onClick={async () => {
              await cancel();
              navigate(`/challenge/new/${mode}`);
            }}
          >
            {t('live.fallbackAsync')}
          </button>
          {expired && (
            <button className="btn ghost" onClick={() => void quick()}>
              {t('live.searchAgain')}
            </button>
          )}
        </div>
      </section>
    );
    return (
      <main className="page">
        <h1>{expired ? t('live.quick') : t('live.searching')}</h1>
        {!expired && (
          <Spinner label={t('live.searchingBody', { seconds: Math.floor(waited / 1000) })} />
        )}
        {!expired && searching !== null && (
          <p className="muted small searching-count">
            {t('live.othersSearching', { count: searching })}
          </p>
        )}
        <p className="muted small">{t('live.noFakePlayers')}</p>
        {/* Alternatives at once when nobody else is searching, otherwise after a short wait. */}
        {(expired || searching === 0 || waited >= FALLBACK_AFTER_MS) && fallback}
        <button className="btn ghost" onClick={() => void cancel()}>
          {expired ? t('common.close') : t('common.cancel')}
        </button>
      </main>
    );
  }

  return (
    <main className="page">
      <BackLink />
      <h1>{t('online.liveDuel')}</h1>
      <fieldset className="mode-picker">
        <legend>{t('setup.mode')}</legend>
        {(['classic', 'polyglot', 'duel', 'sprint'] as const).map((m) => (
          <button
            type="button"
            key={m}
            className={`mode-card${m === mode ? ' selected' : ''}`}
            aria-pressed={m === mode}
            onClick={() => {
              setMode(m);
              const d = MODES[m];
              if (langs.length > d.maxLanguages) setLangs(langs.slice(0, d.maxLanguages));
            }}
          >
            <strong>{t(`modes.${m}.name`)}</strong>
            <span className="muted small">{t(`modes.${m}.short`)}</span>
          </button>
        ))}
      </fieldset>
      <LanguagePicker
        value={langs}
        onChange={setLangs}
        max={def.maxLanguages}
        label={
          def.languagePolicy === 'shared'
            ? t('setup.languagesShared', { min: def.minLanguages, max: def.maxLanguages })
            : t('live.languagesSecret')
        }
      />
      <ContractPreview mode={mode} langs={langs} />
      {error && <p className="message error">{error}</p>}
      {busy ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="results-actions">
          <button
            className="btn primary big"
            disabled={!!selectionError}
            onClick={() => void quick()}
          >
            {t('live.quick')}
          </button>
          <button
            className="btn ghost"
            disabled={def.languagePolicy === 'shared' && !!selectionError}
            onClick={() => void createRoom()}
          >
            {t('live.createRoom')}
          </button>
        </div>
      )}
      <p className="muted small">{t('live.rankedNote')}</p>
    </main>
  );
}

export function LiveJoin({ params }: { params: { token: string } }) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    ensureSession()
      .then(() => api<{ matchId: string }>('POST', '/api/live/join', { token: params.token }))
      .then((r) => navigate(`/match/${r.matchId}`, { replace: true }))
      .catch((e: unknown) =>
        setError(
          e instanceof ApiError && e.code === 'room_full'
            ? t('live.roomFull')
            : t('live.roomNotFound'),
        ),
      );
  }, [params.token, navigate, t]);
  if (error)
    return (
      <main className="page">
        <p className="message error">{error}</p>
        <Link href="/live">{t('online.liveDuel')}</Link>
      </main>
    );
  return <Spinner label={t('common.loading')} />;
}
